/**
 * Reference catalogs — TanStack Query hooks (AK-231 Pilot).
 *
 * Real-backend by default (AK-242 aligned): мок только при VITE_REFERENCE_USE_MOCK=true
 * из __mocks__/reference.ts. Когда backend (миграция 046 + расширение
 * /api/v1/reference/*) доедет до прода — switch на false.
 *
 * Pattern mirror'ит usePlatformAdminQueries.ts — единый стиль для Platform Admin.
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '@/lib/api';
import { usePagedInfinite } from '@/pages/platform-admin/reference/hooks/usePagedInfinite';
import {
  archiveCropMock,
  createCategoryMock,
  createCropMock,
  createGoalMock,
  createPhenophaseMock,
  deleteCategoryMock,
  deleteGoalMock,
  deletePhenophaseMock,
  fetchCategoriesMock,
  fetchCropsListMock,
  fetchGoalsMock,
  fetchPhenophasesMock,
  reorderPhenophasesMock,
  updateCategoryMock,
  updateCropMock,
  updateGoalMock,
  type CategoriesListResponse,
  type CategoryCreatePayload,
  type Crop,
  type CropCategory,
  type CropCreatePayload,
  type CropUpdatePayload,
  type CropsListQuery,
  type CropsListResponse,
  type GoalCreatePayload,
  type GoalsListResponse,
  type GrowingGoal,
  type Phenophase,
  type PhenophaseCreatePayload,
  type PhenophasesListResponse,
} from '@/pages/platform-admin/reference/__mocks__/reference';
import type {
  CropGroup,
  CropGroupCreatePayload,
  CropGroupsEnvelope,
} from '@/pages/platform-admin/reference/types';

const USE_MOCK =
  (import.meta.env.VITE_REFERENCE_USE_MOCK as string | undefined) === 'true';

export const referenceKeys = {
  crops: (q: CropsListQuery) => ['reference', 'crops', q] as const,
  categories: () => ['reference', 'categories'] as const,
  groups: () => ['reference', 'crop-groups'] as const,
  goals: () => ['reference', 'growing-goals'] as const,
  phenophases: (cropId: string) => ['reference', 'phenophases', cropId] as const,
};

function buildCropsParams(q: CropsListQuery): string {
  const params = new URLSearchParams();
  if (q.search) params.set('search', q.search);
  if (q.category_id && q.category_id !== 'all') params.set('category_id', q.category_id);
  if (q.group_id && q.group_id !== 'all') params.set('group_id', q.group_id);
  if (q.season_phase) params.set('season_phase', q.season_phase);
  if (typeof q.is_winter === 'boolean') params.set('is_winter', String(q.is_winter));
  if (typeof q.is_perennial === 'boolean') params.set('is_perennial', String(q.is_perennial));
  if (q.include_archived) params.set('include_archived', 'true');
  params.set('page', String(q.page ?? 1));
  params.set('page_size', String(q.page_size ?? 50));
  return params.toString();
}

// ---------- Crops ----------

export function useCrops(query: CropsListQuery): UseQueryResult<CropsListResponse, Error> {
  return useQuery({
    queryKey: referenceKeys.crops(query),
    queryFn: () =>
      USE_MOCK
        ? fetchCropsListMock(query)
        : api.get<CropsListResponse>(`/api/v1/reference/crops?${buildCropsParams(query)}`),
    staleTime: 30_000,
  });
}

/**
 * Бесконечный список культур (AK-574).
 *
 * Экран культур просил `page=1&page_size=100` и рисовал рядом серверный
 * `total`: «найдено 164» и 100 строк без намёка на обрыв. Здесь курсор —
 * `offset` (бэкенд принимает обе схемы; `page` выигрывает у `limit/offset`,
 * поэтому его тут намеренно НЕ передаём).
 */
export function useCropsInfinite(query: CropsListQuery) {
  const { page: _page, page_size: _pageSize, ...filter } = query;
  return usePagedInfinite<Crop>(
    ['reference', 'crops', 'infinite', filter] as const,
    (offset, limit) => {
      const params = new URLSearchParams(buildCropsParams(filter));
      params.delete('page');
      params.delete('page_size');
      params.set('limit', String(limit));
      params.set('offset', String(offset));
      return `/api/v1/reference/crops?${params.toString()}`;
    },
  );
}

/**
 * AK-259 (B1-lesson, PR#290) — single-crop detail fetch для редактора.
 *
 * Форма культуры заполняется из detail (`GET /reference/crops/{id}`), а не из
 * ListItem: список может быть slim'нут в будущем, тогда edit-форма потеряет
 * поля (group_id / season_phase / growing_goal_ids / tkw). Detail — source of
 * truth для всех редактируемых полей. `enabled` только при наличии id.
 *
 * Mock-режим: list-фикстура у нас full-fidelity (Crop = list+detail), поэтому
 * отдельного detail-мока нет — hook не вызывается (USE_MOCK ветка возвращает
 * сам объект из списка). Чтобы не плодить фикстуру, в mock detail = reject и
 * редактор fallback'ается на переданный list-item.
 */
export function useCrop(cropId: string | null): UseQueryResult<Crop, Error> {
  return useQuery({
    queryKey: ['reference', 'crop', cropId] as const,
    queryFn: () => api.get<Crop>(`/api/v1/reference/crops/${cropId}`),
    enabled: Boolean(cropId) && !USE_MOCK,
    staleTime: 30_000,
  });
}

export function useCreateCrop() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CropCreatePayload) =>
      USE_MOCK
        ? createCropMock(payload)
        : api.post<Crop>('/api/v1/reference/crops', payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reference', 'crops'] });
      qc.invalidateQueries({ queryKey: referenceKeys.categories() });
    },
  });
}

export function useUpdateCrop() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CropUpdatePayload }) =>
      USE_MOCK
        ? updateCropMock(id, payload)
        : api.patch<Crop>(`/api/v1/reference/crops/${id}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reference', 'crops'] });
    },
  });
}

export function useArchiveCrop() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      USE_MOCK
        ? archiveCropMock(id)
        : api.post<void>(`/api/v1/reference/crops/${id}/archive`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reference', 'crops'] });
    },
  });
}

/**
 * AK-248 — merge culture (source) into another (target).
 *
 * Serializes `MergeSummary` от `POST /reference/crops/{source}/merge-into/{target}`
 * (AK-247 backend). При `dry_run: true` backend выполняет работу в транзакции и
 * откатывает — counts реальные, ничего не персистится (preview). При
 * `dry_run: false` — repoint всех FK + soft-archive source, commit.
 *
 * onSuccess инвалидирует список culture (source пропадёт / станет архивной).
 * dry-run-вызовы НЕ инвалидируют (ничего не поменялось) — обходим через
 * проверку variables.dryRun.
 *
 * Mock-режим (VITE_REFERENCE_USE_MOCK=true): merge endpoint мокается лишь
 * грубо — нет смысла дублировать FK-repoint логику бэкенда в фикстурах,
 * поэтому в mock возвращаем понятную ошибку (фича требует реального backend).
 */
export interface CropMergeResult {
  source_id: string;
  target_id: string;
  dry_run: boolean;
  repointed: Record<string, number>;
  deleted_conflicts: Record<string, number>;
  total_repointed: number;
  total_deleted: number;
  archived: boolean;
  no_op: boolean;
  message: string;
}

export function useMergeCrops() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      sourceId,
      targetId,
      dryRun = false,
    }: {
      sourceId: string;
      targetId: string;
      dryRun?: boolean;
    }) => {
      if (USE_MOCK) {
        return Promise.reject(
          new Error(
            'Объединение культур доступно только с реальным backend (VITE_REFERENCE_USE_MOCK=false).',
          ),
        );
      }
      return api.post<CropMergeResult>(
        `/api/v1/reference/crops/${sourceId}/merge-into/${targetId}`,
        { dry_run: dryRun },
      );
    },
    onSuccess: (_data, variables) => {
      // dry-run ничего не меняет в БД — не дёргаем список, чтобы preview
      // не вызывал лишний refetch и мерцание таблицы.
      if (variables.dryRun) return;
      qc.invalidateQueries({ queryKey: ['reference', 'crops'] });
    },
  });
}

/**
 * AK-271 — атомарное объединение N культур-источников в один эталон (target).
 *
 * Serializes `BatchMergeSummary` от `POST /reference/crops/merge`. Эталон
 * (первый выбранный) выживает; источники перепривязываются + архивируются в
 * ОДНОЙ транзакции — любой сбой откатывает весь батч (ни один не слит).
 * `dry_run: true` → суммарный preview без персиста.
 *
 * onSuccess (не dry-run) инвалидирует список — источники уйдут в архив,
 * эталон останется; список обновится без перезагрузки страницы.
 */
export interface CropMergeBatchResult {
  target_id: string;
  source_ids: string[];
  dry_run: boolean;
  per_source: CropMergeResult[];
  archived: string[];
  total_repointed: number;
  total_deleted: number;
  source_count: number;
  message: string;
}

export function useMergeCropsBatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      targetId,
      sourceIds,
      dryRun = false,
    }: {
      targetId: string;
      sourceIds: string[];
      dryRun?: boolean;
    }) => {
      if (USE_MOCK) {
        return Promise.reject(
          new Error(
            'Объединение культур доступно только с реальным backend (VITE_REFERENCE_USE_MOCK=false).',
          ),
        );
      }
      return api.post<CropMergeBatchResult>('/api/v1/reference/crops/merge', {
        target_id: targetId,
        source_ids: sourceIds,
        dry_run: dryRun,
      });
    },
    onSuccess: (_data, variables) => {
      if (variables.dryRun) return;
      qc.invalidateQueries({ queryKey: ['reference', 'crops'] });
    },
  });
}

// ---------- Categories ----------

export function useCategories(): UseQueryResult<CategoriesListResponse, Error> {
  return useQuery({
    queryKey: referenceKeys.categories(),
    queryFn: () =>
      USE_MOCK
        ? fetchCategoriesMock()
        : api.get<CategoriesListResponse>('/api/v1/reference/crop-categories'),
    staleTime: 60_000,
  });
}

export function useCreateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: CategoryCreatePayload) =>
      USE_MOCK
        ? createCategoryMock(p)
        : api.post<CropCategory>('/api/v1/reference/crop-categories', p),
    onSuccess: () => qc.invalidateQueries({ queryKey: referenceKeys.categories() }),
  });
}

export function useUpdateCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CategoryCreatePayload }) =>
      USE_MOCK
        ? updateCategoryMock(id, payload)
        : api.patch<CropCategory>(`/api/v1/reference/crop-categories/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: referenceKeys.categories() }),
  });
}

export function useDeleteCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      USE_MOCK
        ? deleteCategoryMock(id)
        : api.delete<void>(`/api/v1/reference/crop-categories/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: referenceKeys.categories() }),
  });
}

// ---------- Crop groups (AK-259 — always real backend, no mock) ----------

export function useCropGroups(): UseQueryResult<CropGroupsEnvelope, Error> {
  return useQuery({
    queryKey: referenceKeys.groups(),
    queryFn: () => api.get<CropGroupsEnvelope>('/api/v1/reference/crop-groups'),
    staleTime: 60_000,
  });
}

export function useCreateCropGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: CropGroupCreatePayload) =>
      api.post<CropGroup>('/api/v1/reference/crop-groups', p),
    onSuccess: () => qc.invalidateQueries({ queryKey: referenceKeys.groups() }),
  });
}

export function useUpdateCropGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: CropGroupCreatePayload }) =>
      api.patch<CropGroup>(`/api/v1/reference/crop-groups/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: referenceKeys.groups() }),
  });
}

export function useDeleteCropGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/v1/reference/crop-groups/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: referenceKeys.groups() }),
  });
}

// ---------- Growing goals ----------

export function useGoals(): UseQueryResult<GoalsListResponse, Error> {
  return useQuery({
    queryKey: referenceKeys.goals(),
    queryFn: () =>
      USE_MOCK
        ? fetchGoalsMock()
        : api.get<GoalsListResponse>('/api/v1/reference/growing-goals'),
    staleTime: 60_000,
  });
}

export function useCreateGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (p: GoalCreatePayload) =>
      USE_MOCK
        ? createGoalMock(p)
        : api.post<GrowingGoal>('/api/v1/reference/growing-goals', p),
    onSuccess: () => qc.invalidateQueries({ queryKey: referenceKeys.goals() }),
  });
}

export function useUpdateGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: GoalCreatePayload }) =>
      USE_MOCK
        ? updateGoalMock(id, payload)
        : api.patch<GrowingGoal>(`/api/v1/reference/growing-goals/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: referenceKeys.goals() }),
  });
}

export function useDeleteGoal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      USE_MOCK
        ? deleteGoalMock(id)
        : api.delete<void>(`/api/v1/reference/growing-goals/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: referenceKeys.goals() }),
  });
}

// ---------- Phenophases ----------

/**
 * Список фенофаз культуры.
 *
 * `envelope=true` обязателен: бэкенд по умолчанию отдаёт ГОЛЫЙ массив, а
 * конверт `{items}` включается параметром (`crops.py::list_phenophases`,
 * AK-242). Фронт его не передавал и читал `data.items` у массива — вкладка
 * «Фенофазы» в редакторе культуры падала с TypeError (аудит админки 12.08).
 *
 * Чиним со стороны фронта, а не меняем дефолт бэка: параметр заведён ровно
 * под этот экран, а «переключить дефолт» — контрактная правка, которая
 * молча ломает любого другого потребителя голого массива (мобильный клиент,
 * скрипты) без единого теста, который бы это поймал.
 */
export function usePhenophases(
  cropId: string | null,
): UseQueryResult<PhenophasesListResponse, Error> {
  return useQuery({
    queryKey: referenceKeys.phenophases(cropId ?? ''),
    queryFn: () =>
      USE_MOCK
        ? fetchPhenophasesMock(cropId ?? '')
        : api.get<PhenophasesListResponse>(
            `/api/v1/reference/crops/${cropId}/phenophases?envelope=true`,
          ),
    enabled: Boolean(cropId),
    staleTime: 30_000,
  });
}

export function useCreatePhenophase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      cropId,
      payload,
    }: {
      cropId: string;
      payload: PhenophaseCreatePayload;
    }) =>
      USE_MOCK
        ? createPhenophaseMock(cropId, payload)
        : api.post<Phenophase>(
            `/api/v1/reference/crops/${cropId}/phenophases`,
            payload,
          ),
    onSuccess: (_data, variables) =>
      qc.invalidateQueries({ queryKey: referenceKeys.phenophases(variables.cropId) }),
  });
}

/**
 * Удаление фенофазы.
 *
 * Роут — плоский `/reference/phenophases/{id}` (crops.py::delete_phenophase),
 * как и PATCH. Вложенного `/crops/{id}/phenophases/{pid}` в бэкенде нет и
 * не было: запрос уходил в 404, тост об успехе всё равно показывался, фаза
 * возвращалась после инвалидации. `cropId` остаётся в аргументах — он нужен
 * для инвалидации кэша списка, но в URL не участвует.
 */
export function useDeletePhenophase() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ cropId, phenophaseId }: { cropId: string; phenophaseId: string }) =>
      USE_MOCK
        ? deletePhenophaseMock(cropId, phenophaseId)
        : api.delete<void>(`/api/v1/reference/phenophases/${phenophaseId}`),
    onSuccess: (_data, variables) =>
      qc.invalidateQueries({ queryKey: referenceKeys.phenophases(variables.cropId) }),
  });
}

/**
 * Bulk reorder phenophases (AK-240).
 *
 * Атомарный endpoint — одной транзакцией проставит display_order = 1..N
 * по позиции в массиве `order`. Используется per-scale (BBCH / Zadoks / other),
 * id отсутствующих scale остаются без изменений.
 */
export function useReorderPhenophases() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ cropId, order }: { cropId: string; order: string[] }) =>
      USE_MOCK
        ? reorderPhenophasesMock(cropId, order)
        : api.put<void>(
            `/api/v1/reference/crops/${cropId}/phenophases/order`,
            { order },
          ),
    onSuccess: (_data, variables) =>
      qc.invalidateQueries({ queryKey: referenceKeys.phenophases(variables.cropId) }),
  });
}
